import { ref, computed } from 'vue';
import type { Tutorial } from '../types';

/**
 * Page Detection Composable
 * Handles page identification and route-specific tutorial management
 */
export function usePageDetection() {
  
  // Current page information
  const currentUrl = ref(window.location.href);
  const currentPathname = ref(window.location.pathname);
  const currentHash = ref(window.location.hash);
  const currentSearch = ref(window.location.search);

  /**
   * Updates current page information
   */
  const updateCurrentPage = (): void => {
    currentUrl.value = window.location.href;
    currentPathname.value = window.location.pathname;
    currentHash.value = window.location.hash;
    currentSearch.value = window.location.search;
  };

  /**
   * Gets the current page identifier
   */
  const getCurrentPage = (): string => {
    return currentPathname.value;
  };

  /**
   * Gets a normalized page key for tutorial registration
   */
  const getPageKey = (url?: string): string => {
    const targetUrl = url || currentPathname.value;
    
    // Remove trailing slashes and normalize
    return targetUrl.replace(/\/$/, '') || '/';
  };

  /**
   * Checks if current page matches a pattern
   */
  const matchesPagePattern = (pattern: string): boolean => {
    const currentPage = getCurrentPage();
    
    // Simple pattern matching
    if (pattern === '*') return true;
    if (pattern === currentPage) return true;
    
    // Support for wildcard patterns
    if (pattern.includes('*')) {
      const regexPattern = pattern
        .replace(/\*/g, '.*')
        .replace(/\?/g, '.');
      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(currentPage);
    }
    
    return false;
  };

  /**
   * Gets page metadata
   */
  const getPageMetadata = () => {
    const title = document.title;
    const meta = {
      title,
      description: '',
      keywords: ''
    };

    // Try to get meta description
    const descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      meta.description = (descriptionMeta as HTMLMetaElement).content;
    }

    // Try to get meta keywords
    const keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) {
      meta.keywords = (keywordsMeta as HTMLMetaElement).content;
    }

    return meta;
  };

  /**
   * Detects page type based on URL patterns
   */
  const detectPageType = (): string => {
    const pathname = currentPathname.value.toLowerCase();
    
    // Common page type patterns
    if (pathname === '/' || pathname === '/home') return 'home';
    if (pathname.includes('/dashboard')) return 'dashboard';
    if (pathname.includes('/login')) return 'login';
    if (pathname.includes('/settings')) return 'settings';
    if (pathname.includes('/profile')) return 'profile';
    if (pathname.includes('/admin')) return 'admin';
    if (pathname.includes('/installation')) return 'installation';
    if (pathname.includes('/maintenance')) return 'maintenance';
    if (pathname.includes('/notifications')) return 'notifications';
    if (pathname.includes('/register')) return 'register';
    
    // Default to 'page' for unrecognized patterns
    return 'page';
  };

  /**
   * Gets tutorials filtered for current page
   */
  const getTutorialsForPage = (tutorials: Tutorial[], pageUrl?: string): Tutorial[] => {
    const targetPage = pageUrl || getCurrentPage();
    
    return tutorials.filter(tutorial => {
      if (!tutorial.pageUrl) return false;
      
      // Exact match
      if (tutorial.pageUrl === targetPage) return true;
      
      // Pattern match
      return matchesPagePattern(tutorial.pageUrl);
    });
  };

  /**
   * Sets up page change detection
   */
  const setupPageChangeDetection = (callback?: (newPage: string, oldPage: string) => void): (() => void) => {
    let previousPage = getCurrentPage();
    
    const handlePageChange = () => {
      const newPage = window.location.pathname;
      if (newPage !== previousPage) {
        const oldPage = previousPage;
        updateCurrentPage();
        previousPage = newPage;
        
        if (callback) {
          callback(newPage, oldPage);
        }
        
      }
    };

    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handlePageChange);
    
    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args: Parameters<typeof originalPushState>) {
      originalPushState.apply(history, args);
      setTimeout(handlePageChange, 0);
    };
    
    history.replaceState = function(...args: Parameters<typeof originalReplaceState>) {
      originalReplaceState.apply(history, args);
      setTimeout(handlePageChange, 0);
    };

    // Cleanup function
    return () => {
      window.removeEventListener('popstate', handlePageChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  };

  /**
   * Gets DOM elements that could be useful for tutorials
   */
  const getInteractiveElements = (): Array<{ selector: string; type: string; text: string }> => {
    const elements: Array<{ selector: string; type: string; text: string }> = [];
    
    // Buttons
    document.querySelectorAll('button').forEach((btn, index) => {
      const text = btn.textContent?.trim() || '';
      const id = btn.id;
      const className = btn.className;
      
      let selector = 'button';
      if (id) selector = `#${id}`;
      else if (className) selector = `button.${className.split(' ')[0]}`;
      else selector = `button:nth-child(${index + 1})`;
      
      elements.push({
        selector,
        type: 'button',
        text
      });
    });

    // Input fields
    document.querySelectorAll('input, textarea, select').forEach((input, index) => {
      const type = input.getAttribute('type') || input.tagName.toLowerCase();
      const placeholder = input.getAttribute('placeholder') || '';
      const id = input.id;
      const name = input.getAttribute('name') || '';
      
      let selector = input.tagName.toLowerCase();
      if (id) selector = `#${id}`;
      else if (name) selector = `[name="${name}"]`;
      else selector = `${input.tagName.toLowerCase()}:nth-child(${index + 1})`;
      
      elements.push({
        selector,
        type,
        text: placeholder || name
      });
    });

    // Links
    document.querySelectorAll('a').forEach((link, index) => {
      const text = link.textContent?.trim() || '';
      const href = link.getAttribute('href') || '';
      const id = link.id;
      
      let selector = 'a';
      if (id) selector = `#${id}`;
      else if (href) selector = `a[href="${href}"]`;
      else selector = `a:nth-child(${index + 1})`;
      
      elements.push({
        selector,
        type: 'link',
        text
      });
    });

    return elements;
  };

  // Computed properties
  const pageInfo = computed(() => ({
    url: currentUrl.value,
    pathname: currentPathname.value,
    hash: currentHash.value,
    search: currentSearch.value,
    type: detectPageType(),
    metadata: getPageMetadata()
  }));

  // Initialize
  updateCurrentPage();

  return {
    // State
    currentUrl,
    currentPathname,
    currentHash,
    currentSearch,
    pageInfo,

    // Methods
    updateCurrentPage,
    getCurrentPage,
    getPageKey,
    matchesPagePattern,
    getPageMetadata,
    detectPageType,
    getTutorialsForPage,
    setupPageChangeDetection,
    getInteractiveElements
  };
}
